import { useEffect, useMemo, useRef, useState } from 'react';
import { catalogApi } from '../utils/api';
import { formatDateShort } from '../utils/dateFormat';
import { toast } from './Toast';
import SearchableSelect from './SearchableSelect';
import ExpandCaret from './ExpandCaret';
import Icon from './Icon';
import ConfirmModal from './ConfirmModal';

// אוטומציות סיווג — per-supplier default item-classification rules, in the Monday-style
// "when → then" form: WHEN an item from [supplier] is scanned unclassified →
// assign it [classification]. Inline collapsible card on /item-classifications, matching
// ClassificationSheet's shell (the category-lines-* classes).
//
// An item has exactly one classification, and supplier_id is the table's PRIMARY
// KEY, so "one supplier → at most one rule" is structural — the server upserts by
// supplier, never inserts a duplicate. That is what makes the interaction below
// possible: every write acts on exactly ONE rule, independent of every other one.
//
// SAVED rules render as one compact line — "supplier → classification", a pencil,
// a trash — not the two-open-dropdown form. That form is for the ONE moment you
// are actually choosing a supplier and a classification (adding, or editing one
// existing rule); re-showing it for every already-saved row was the whole
// complaint this replaces, and it made a list of eight rules read as eight
// half-finished forms. At most one row is ever in that form at a time — the same
// one-row-editable-at-a-time idiom ClassificationSheet already uses for renaming.

export default function ClassificationRulesCard({
  suppliers = [], classificationOptions = [], open = true, onToggle, onSaved,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // A Set, not a single id: two deletes can overlap in flight (confirm B
  // while A is still pending), and a scalar let A's finally clear B's busy
  // flag out from under it, re-enabling B's row mid-request.
  const [busyIds, setBusyIds] = useState(() => new Set());
  const markBusy = (id) => setBusyIds(prev => new Set(prev).add(id));
  const clearBusy = (id) => setBusyIds(prev => {
    const next = new Set(prev);
    next.delete(id);
    return next;
  });
  // Load failures only. Save and delete failures are toasts: the list has no
  // height limit, so a message up here could be far above the row being worked on.
  const [error, setError] = useState('');
  const [confirm, setConfirm] = useState(null);
  const loadedOnce = useRef(false);

  // The one row currently open for editing: an existing rule's supplier_id,
  // the sentinel 'new' for a fresh row, or null when nothing is open.
  const [editingId, setEditingId] = useState(null);
  // Only meaningful for the 'new' row — supplier_id is the table's primary
  // key, so an EXISTING rule's supplier can never change; editing one only
  // ever touches draftClassificationId (see commitDraft/startEdit below).
  const [newSupplierId, setNewSupplierId] = useState('');
  const [draftClassificationId, setDraftClassificationId] = useState('');

  // Load the saved rules once (on first mount).
  useEffect(() => {
    if (loadedOnce.current) return;
    loadedOnce.current = true;
    catalogApi.getRules()
      .then(r => setRows(r.data.rules || []))
      .catch(e => setError(e?.response?.data?.error || 'שגיאה בטעינת הכללים'))
      .finally(() => setLoading(false));
  }, []);

  // Suppliers already ruled are excluded from the ADD picker — not validated
  // against after the fact. A supplier that already has a rule is reached by
  // editing that rule, never by "adding" a second, conflicting one; this makes
  // the old two-rules-one-supplier conflict structurally impossible to create
  // through the UI, rather than something the form catches and blocks on.
  const availableSupplierOptions = useMemo(() => {
    const used = new Set(rows.map(r => r.supplier_id));
    return suppliers.filter(s => !used.has(Number(s.value)));
  }, [suppliers, rows]);

  // The same colour the picker shows: a classification with no stored colour gets
  // its fallback from the page's options, so a saved row must not read it raw.
  const colorByClassification = useMemo(
    () => new Map(classificationOptions.map(o => [Number(o.value), o.color])),
    [classificationOptions],
  );

  const headerSummary = rows.length === 0 ? '' : rows.length === 1 ? 'כלל אחד' : `${rows.length} כללים`;

  const startAdd = () => {
    setEditingId('new');
    setNewSupplierId('');
    setDraftClassificationId('');
  };
  const startEdit = (rule) => {
    setEditingId(rule.supplier_id);
    setDraftClassificationId(rule.classification_id);
  };
  const cancelEdit = () => {
    setEditingId(null);
    setNewSupplierId('');
    setDraftClassificationId('');
  };

  const commitDraft = async () => {
    const supplierId = editingId === 'new' ? newSupplierId : editingId;
    if (!supplierId || !draftClassificationId) {
      toast('יש לבחור ספק וסיווג', 'error');
      return;
    }
    setSaving(true);
    try {
      const r = await catalogApi.saveRule(supplierId, draftClassificationId);
      const saved = r.data.rule;
      setRows(rs => [...rs.filter(x => x.supplier_id !== saved.supplier_id), saved]
        .sort((a, b) => (a.supplier_name || '').localeCompare(b.supplier_name || '', 'he')));
      setError('');
      const n = r.data.backfilled || 0;
      toast(n > 0 ? `הכלל נשמר · סווגו ${n} מוצרים קיימים` : 'הכלל נשמר');
      cancelEdit();
      await onSaved?.();
    } catch (e) {
      toast(e?.response?.data?.error || 'שגיאה בשמירת הכלל', 'error');
    } finally {
      setSaving(false);
    }
  };

  const deleteRule = (rule) => {
    setConfirm({
      title: 'מחיקת כלל אוטומציה',
      body: [
        `${rule.supplier_name} · ${rule.classification_name}`,
        'מוצרים שכבר סווגו לא ישתנו — הכלל רק מפסיק לסווג אוטומטית מוצרים חדשים וקיימים מהספק הזה מעכשיו.',
      ],
      confirmLabel: 'מחיקת הכלל',
      cancelLabel: 'ביטול',
      danger: true,
      onConfirm: async () => {
        setConfirm(null);
        markBusy(rule.supplier_id);
        try {
          await catalogApi.deleteRule(rule.supplier_id);
          setRows(rs => rs.filter(x => x.supplier_id !== rule.supplier_id));
          if (editingId === rule.supplier_id) cancelEdit();
          await onSaved?.();
        } catch (e) {
          toast(e?.response?.data?.error || 'שגיאה במחיקת הכלל', 'error');
        } finally {
          clearBusy(rule.supplier_id);
        }
      },
    });
  };

  return (
    <>
      {confirm && <ConfirmModal {...confirm} onCancel={() => setConfirm(null)} />}
      <section className={`card category-lines-card${open ? '' : ' is-collapsed'}`}>
        <button
          type="button" className="category-lines-toggle"
          onClick={onToggle} aria-expanded={open} aria-controls="classification-rules-body"
        >
          <span className="category-lines-title">אוטומציות סיווג</span>
          <span className="category-lines-summary">{headerSummary}</span>
          <ExpandCaret open={open} size={14} />
        </button>

        {open && (
        <div id="classification-rules-body" dir="rtl">
          {/* ONE line. This used to be two paragraphs — a subtitle and an
              empty-state sentence — that said the same thing in different
              words, which is why the card read as wordy: the reader paid
              twice for one idea. The empty-state copy is gone; this line
              carries it, including the part people actually ask about
              ("what about products I already have?"). */}
          <p style={{ margin: '0 0 14px', fontSize: 12.5, color: 'var(--ink-muted)', lineHeight: 1.6 }}>
            בחרו ספק — והמוצרים שלו יסווגו אוטומטית, גם החדשים וגם קיימים שטרם סווגו.
          </p>

          {error && (
            <div className="badge-red" style={{ display: 'block', marginBottom: 12, padding: '8px 12px', borderRadius: 8 }}>
              {error}
            </div>
          )}

          {loading ? (
            <div className="loading-container"><div className="spinner" /></div>
          ) : (
            <>

              {/* Add sits above the list: the list has no inner scroll (see below), so
                  with many rules a button under it would be a long way down. Editing a
                  rule only disables it: removing it would shift every row up. */}
              {editingId !== 'new' && (
                availableSupplierOptions.length > 0 ? (
                  /* With no rules yet this is the ONLY thing to do on the
                     card, so it gets the primary treatment. The dashed
                     .rule-add is right for "add another" sitting above a
                     list — but alone on an empty card it read as disabled
                     (muted grey text, dashed border, nothing else to
                     contrast it against). */
                  rows.length === 0 ? (
                    <button type="button" className="btn btn-primary" onClick={startAdd}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <Icon name="plus" size={15} /> הוספת כלל ראשון
                    </button>
                  ) : (
                    <button type="button" className="rule-add" onClick={startAdd} disabled={editingId !== null} style={{ marginBottom: 10 }}>
                      <Icon name="plus" size={14} /> הוסף כלל
                    </button>
                  )
                ) : rows.length > 0 ? (
                  <p style={{ margin: '0 0 10px', fontSize: 12.5, color: 'var(--ink-muted)' }}>
                    לכל הספקים כבר יש כלל אוטומציה.
                  </p>
                ) : null
              )}

              {editingId === 'new' && (
                <div className="rule-row" style={{ marginBottom: rows.length > 0 ? 10 : 0 }}>
                  <div className="rule-clause rule-clause-when">
                    <span className="rule-lbl">כאשר פריט מספק</span>
                    <div className="rule-pick rule-pick-supplier">
                      <SearchableSelect
                        value={newSupplierId}
                        options={availableSupplierOptions}
                        placeholder="בחר ספק"
                        ariaLabel="ספק לכלל"
                        revealMenu
                        onChange={setNewSupplierId}
                      />
                    </div>
                    <span className="rule-lbl">אינו מסווג</span>
                  </div>
                  <div className="rule-clause">
                    <Icon name="chevron-inline" size={14} className="rule-arrow" style={{ transform: 'rotate(180deg)' }} />
                    <span className="rule-lbl">שייך לסיווג</span>
                    <div className="rule-pick rule-pick-cat">
                      <SearchableSelect
                        value={draftClassificationId}
                        options={classificationOptions}
                        placeholder="בחר סיווג"
                        ariaLabel="סיווג לכלל"
                        revealMenu
                        onChange={setDraftClassificationId}
                      />
                    </div>
                    <button type="button" className="btn btn-primary btn-sm"
                      onClick={commitDraft} disabled={saving}>
                      שמירה
                    </button>
                    <button type="button" className="btn btn-secondary btn-sm"
                      onClick={cancelEdit} disabled={saving}>
                      ביטול
                    </button>
                  </div>
                </div>
              )}

              {/* No max-height or overflow here, ever. An open dropdown is absolutely
                  positioned inside its row, so a scrolling list clipped it: with one
                  rule only about 1.5 options showed. The list grows with the card
                  and the dropdown floats over whatever is below it. */}
              {rows.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {rows.map(rule => (
                    editingId === rule.supplier_id ? (
                      <div key={rule.supplier_id} className="rule-row" style={{ borderColor: '#94a3b8' }}>
                        <div className="rule-clause rule-clause-when">
                          <span className="rule-lbl">כאשר פריט מספק</span>
                          <span className="rule-lbl" style={{ fontWeight: 600 }}>{rule.supplier_name}</span>
                          <span className="rule-lbl">אינו מסווג</span>
                        </div>
                        <div className="rule-clause">
                          <Icon name="chevron-inline" size={14} className="rule-arrow" style={{ transform: 'rotate(180deg)' }} />
                          <span className="rule-lbl">שייך לסיווג</span>
                          <div className="rule-pick rule-pick-cat">
                            <SearchableSelect
                              value={draftClassificationId}
                              options={classificationOptions}
                              placeholder="בחר סיווג"
                              ariaLabel="סיווג לכלל"
                              revealMenu
                              onChange={setDraftClassificationId}
                            />
                          </div>
                          <button type="button" className="btn btn-primary btn-sm"
                            onClick={commitDraft} disabled={saving}>
                            שמירה
                          </button>
                          <button type="button" className="btn btn-secondary btn-sm"
                            onClick={cancelEdit} disabled={saving}>
                            ביטול
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div key={rule.supplier_id} className="rule-row rule-row-compact">
                        <div className="rule-summary">
                          <span className="rule-summary-dot" style={{ background: colorByClassification.get(rule.classification_id) || rule.classification_color || '#94a3b8' }} />
                          <span className="rule-summary-text">
                            <span>{rule.supplier_name}</span>
                            <Icon name="chevron-inline" size={12} className="rule-arrow" style={{ transform: 'rotate(180deg)', margin: '0 6px' }} />
                            <span>{rule.classification_name}</span>
                          </span>
                          {rule.untagged_count > 0 && (
                            <span className="badge badge-blue" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                              {rule.untagged_count} ממתינים
                            </span>
                          )}
                        </div>
                        <span className="rule-meta">
                          {rule.created_by_email || '—'}
                          {rule.created_at ? ` · ${formatDateShort(rule.created_at)}` : ''}
                        </span>
                        <button type="button" className="rule-icon-btn"
                          title="עריכה" aria-label={`עריכת כלל עבור ${rule.supplier_name}`}
                          onClick={() => startEdit(rule)} disabled={busyIds.has(rule.supplier_id) || editingId != null}>
                          <Icon name="edit" size={13} />
                        </button>
                        <button type="button" className="rule-icon-btn danger"
                          title="מחיקה" aria-label={`מחיקת כלל עבור ${rule.supplier_name}`}
                          onClick={() => deleteRule(rule)} disabled={busyIds.has(rule.supplier_id) || editingId != null}>
                          <Icon name="trash" size={13} />
                        </button>
                      </div>
                    )
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        )}
      </section>
    </>
  );
}
